import React, { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, setActiveFinYear } from "@/lib/api";
import {
  LayoutDashboard, Building2, Users, FileSignature, ReceiptText, Wallet, AlertTriangle,
  TrendingDown, PiggyBank, BarChart3, CalendarDays, Bell, FolderOpen, Settings as Cog,
  Search, LogOut, Menu, X, ShieldCheck, Landmark, Crown, DatabaseBackup, ChevronDown, Inbox, UserCog, DoorOpen,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

const MAIN = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true, perm: "dashboard.view" },
  { to: "/properties", label: "Properties", icon: Building2, perm: "properties.view" },
  { to: "/tenants", label: "Tenants", icon: Users, perm: "tenants.view" },
  { to: "/agreements", label: "Agreements", icon: FileSignature, perm: "agreements.view" },
  { to: "/invoices", label: "Invoices", icon: ReceiptText, perm: "invoices.view" },
  { to: "/payments", label: "Payments", icon: Wallet, perm: "payments.view" },
  { to: "/tenant-approvals", label: "Approval Inbox", icon: Inbox, perm: "approvals.view" },
  { to: "/outstanding", label: "Outstanding Rent", icon: AlertTriangle, perm: "outstanding.view" },
  { to: "/expenses", label: "Expenses", icon: TrendingDown, perm: "expenses.view" },
  { to: "/calendar", label: "Calendar", icon: CalendarDays, perm: "calendar.view" },
  { to: "/notifications", label: "Notifications", icon: Bell, perm: "notifications.view" },
];
const REPORTS_GROUP = [
  { to: "/deposits", label: "Security Deposits", icon: PiggyBank, perm: "deposits.view" },
  { to: "/banking", label: "Cash & Bank", icon: Landmark, perm: "banking.view" },
  { to: "/reports", label: "Reports", icon: BarChart3, perm: "reports.view" },
  { to: "/vacant-units", label: "Vacant Units", icon: DoorOpen, perm: "reports.view" },
  { to: "/tenant-summary", label: "Tenant Sheet", icon: Users, perm: "tenant_sheet.view" },
  { to: "/documents", label: "Documents", icon: FolderOpen, perm: "documents.view" },
];
const CONTROL_GROUP = [
  { to: "/owners", label: "Business / Owner", icon: UserCog, perm: "owners.view" },
  { to: "/users", label: "Users", icon: ShieldCheck, perm: "users.view" },
  { to: "/backup", label: "Backup & Restore", icon: DatabaseBackup, perm: "backup.view" },
  { to: "/settings", label: "Settings", icon: Cog, perm: "settings.view" },
];

const NavItem = ({ n, onNav, indent }) => (
  <NavLink to={n.to} end={n.end} onClick={onNav} data-testid={`nav-${n.label.toLowerCase().replace(/ /g, "-")}`}
    className={({ isActive }) => cn("flex items-center gap-3 py-2 rounded-md text-sm font-medium transition-colors duration-150",
      indent ? "pl-9 pr-3" : "px-3",
      isActive ? "bg-indigo-600 text-white" : "text-slate-300 hover:bg-slate-800 hover:text-white")}>
    <n.icon className="w-4 h-4 shrink-0" /> {n.label}
  </NavLink>
);
const NavGroup = ({ label, icon: Icon, items, expanded, onToggle, onNav }) => {
  if (!items.length) return null;
  return (
    <div className="pt-2">
      <button onClick={onToggle} data-testid={`navgroup-${label.toLowerCase().replace(/ /g, "-")}`}
        className="w-full flex items-center gap-3 px-3 py-2 rounded-md text-xs font-semibold uppercase tracking-wider text-slate-400 hover:text-white transition-colors">
        <Icon className="w-4 h-4 shrink-0" /><span className="flex-1 text-left">{label}</span>
        <ChevronDown className={cn("w-4 h-4 transition-transform", !expanded && "-rotate-90")} />
      </button>
      {expanded && <div className="space-y-0.5 mt-0.5">{items.map((n) => <NavItem key={n.to} n={n} onNav={onNav} indent />)}</div>}
    </div>
  );
};

const nowD = new Date();
const CUR_FY_START = nowD.getMonth() >= 3 ? nowD.getFullYear() : nowD.getFullYear() - 1;
const FY_OPTIONS = Array.from({ length: 6 }, (_, i) => { const y = CUR_FY_START - i; return `${y}-${String((y + 1) % 100).padStart(2, "0")}`; });

const ROUTE_MAP = { property: "/properties", tenant: "/tenants", invoice: "/invoices", payment: "/payments" };

export default function AppShell({ children }) {
  const { user, logout, can, activeRole, businesses, activeBusiness, switchBusiness, idleMinutes, setIdleMinutes } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [unread, setUnread] = useState(0);
  const [expanded, setExpanded] = useState({ reports: true, control: true });
  const [subOpen, setSubOpen] = useState(false);
  const sub = user?.subscription;
  const daysLeft = sub?.plan_expires_at ? Math.max(0, Math.ceil((new Date(sub.plan_expires_at).getTime() - Date.now()) / 86400000)) : null;
  const toggle = (k) => setExpanded((e) => ({ ...e, [k]: !e[k] }));
  const isAdmin = activeRole === "owner" || activeRole === "admin";
  const mainItems = MAIN.filter((n) => can(n.perm));
  const reportItems = REPORTS_GROUP.filter((n) => can(n.perm));
  const controlItems = CONTROL_GROUP.filter((n) => can(n.perm));
  if (user?.is_super_admin) controlItems.push({ to: "/admin", label: "Super Admin Panel", icon: Crown });

  useEffect(() => {
    api.get("/notifications").then((n) => setUnread(n.filter((x) => x.status === "unread").length)).catch(() => {});
  }, []);
  useEffect(() => {
    if (!q.trim()) { setResults([]); return; }
    const t = setTimeout(() => api.get(`/search?q=${encodeURIComponent(q)}`).then(setResults).catch(() => {}), 250);
    return () => clearTimeout(t);
  }, [q]);

  const Sidebar = (
    <aside className="w-64 bg-slate-950 text-slate-50 flex flex-col h-full">
      <div className="h-16 flex items-center gap-2 px-5 border-b border-slate-800">
        <div className="w-8 h-8 rounded-md bg-indigo-600 flex items-center justify-center"><Building2 className="w-5 h-5" /></div>
        <span className="font-heading font-bold text-lg">RentLedger</span>
      </div>
      <nav className="flex-1 overflow-y-auto py-3 px-3 space-y-0.5">
        {mainItems.map((n) => <NavItem key={n.to} n={n} onNav={() => setOpen(false)} />)}
        <NavGroup label="Reports" icon={BarChart3} items={reportItems} expanded={expanded.reports} onToggle={() => toggle("reports")} onNav={() => setOpen(false)} />
        <NavGroup label="Control Panel" icon={Crown} items={controlItems} expanded={expanded.control} onToggle={() => toggle("control")} onNav={() => setOpen(false)} />
      </nav>
      <div className="p-3 border-t border-slate-800">
        <div className="flex items-center gap-3 px-2 py-2">
          <div className="w-8 h-8 rounded-full bg-indigo-500 flex items-center justify-center text-sm font-semibold">{(user?.name || "A")[0]}</div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium truncate">{user?.name}</p>
            <p className="text-xs text-slate-400 truncate">{user?.email}</p>
          </div>
          <button onClick={() => logout()} data-testid="logout-btn" className="text-slate-400 hover:text-white"><LogOut className="w-4 h-4" /></button>
        </div>
      </div>
    </aside>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="hidden lg:block fixed inset-y-0 left-0 z-50">{Sidebar}</div>
      {open && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)} />
          <div className="relative">{Sidebar}<button onClick={() => setOpen(false)} className="absolute top-4 -right-10 text-white"><X className="w-6 h-6" /></button></div>
        </div>
      )}
      <header className="fixed top-0 right-0 left-0 lg:left-64 h-16 bg-white/80 backdrop-blur-xl border-b border-slate-200 flex items-center gap-3 px-4 sm:px-6 z-40">
        <button className="lg:hidden" onClick={() => setOpen(true)}><Menu className="w-6 h-6 text-slate-700" /></button>
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input data-testid="global-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search properties, tenants, invoices…" className="pl-9 bg-slate-50 border-slate-200" />
          {results.length > 0 && (
            <div className="absolute top-11 left-0 right-0 bg-white border border-slate-200 rounded-md shadow-lg max-h-80 overflow-y-auto z-50">
              {results.map((r, i) => (
                <button key={i} onClick={() => { nav(ROUTE_MAP[r.type] || "/"); setQ(""); }} className="w-full flex items-center justify-between px-3 py-2 hover:bg-slate-50 text-left">
                  <div><p className="text-sm font-medium text-slate-800">{r.label}</p><p className="text-xs text-slate-400 capitalize">{r.type} · {r.sub}</p></div>
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="flex-1" />
        {sub && (
          <button onClick={() => setSubOpen(true)} data-testid="subscription-chip" title="View subscription details"
            className={cn("hidden sm:flex items-center gap-1.5 h-9 px-3 rounded-md border text-xs font-medium transition-colors",
              sub.expired ? "border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100" : daysLeft != null && daysLeft <= 7 ? "border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100" : "border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100")}>
            <Crown className="w-3.5 h-3.5" />
            {sub.expired ? "Plan expired" : daysLeft == null ? (sub.plan_label || "Active plan") : `${sub.plan_label || "Plan"} · ${daysLeft}d left`}
          </button>
        )}
        {activeBusiness && (
          <select data-testid="business-switcher" value={activeBusiness.id}
            onChange={(e) => { const v = e.target.value; if (v === "__new__") { sessionStorage.setItem("force_onboarding", "1"); window.location.reload(); } else switchBusiness(v); }}
            className="hidden sm:block h-9 rounded-md border border-slate-200 bg-slate-50 px-3 text-sm font-medium text-slate-700 max-w-[220px] focus:outline-none focus:ring-2 focus:ring-indigo-500">
            {businesses.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            <option value="__new__">＋ Create / switch business…</option>
          </select>
        )}
        <select data-testid="fy-selector" value={localStorage.getItem("fin_year") || ""}
          onChange={(e) => { const v = e.target.value; localStorage.setItem("fin_year", v); setActiveFinYear(v); window.location.reload(); }}
          title="Financial Year (India · Apr–Mar) — filters invoices, payments, expenses & dashboard"
          className="hidden md:block h-9 rounded-md border border-slate-200 bg-slate-50 px-2 text-xs font-medium text-slate-600 focus:outline-none focus:ring-2 focus:ring-indigo-500">
          <option value="">All Data</option>
          {FY_OPTIONS.map((f) => <option key={f} value={f}>FY {f}</option>)}
        </select>
        <select data-testid="idle-timeout" value={idleMinutes} onChange={(e) => setIdleMinutes(Number(e.target.value))} title="Auto-logout after inactivity"
          className="hidden md:block h-9 rounded-md border border-slate-200 bg-slate-50 px-2 text-xs font-medium text-slate-600 focus:outline-none focus:ring-2 focus:ring-indigo-500">
          <option value={3}>3 min idle</option><option value={5}>5 min idle</option><option value={10}>10 min idle</option>
        </select>
        <button onClick={() => nav("/notifications")} data-testid="header-notifications" className="relative p-2 rounded-md hover:bg-slate-100">
          <Bell className="w-5 h-5 text-slate-600" />
          {unread > 0 && <span className="absolute -top-0.5 -right-0.5 w-4 h-4 rounded-full bg-rose-500 text-white text-[10px] flex items-center justify-center">{unread}</span>}
        </button>
      </header>
      <main className="pt-16 lg:pl-64 min-h-screen">
        {user?.subscription?.expired && (
          <div className="bg-rose-600 text-white text-sm px-4 py-2 text-center" data-testid="expired-banner">Your access has expired. Please contact the administrator to renew your plan.</div>
        )}
        <div className="p-4 sm:p-6 lg:p-8">{children}</div>
      </main>
      <Dialog open={subOpen} onOpenChange={setSubOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><Crown className="w-4 h-4 text-amber-500" /> Subscription</DialogTitle></DialogHeader>
          {sub && (
            <div className="space-y-3 text-sm" data-testid="subscription-dialog">
              <div className="flex justify-between"><span className="text-slate-500">Plan</span><span className="font-medium text-slate-800">{sub.plan_label || "Full"}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Status</span><span className={cn("font-medium capitalize", sub.expired ? "text-rose-600" : "text-emerald-600")} data-testid="sub-status">{sub.expired ? "Expired" : sub.plan_status || "active"}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Expires on</span><span className="font-medium text-slate-800" data-testid="sub-expiry">{sub.plan_expires_at ? new Date(sub.plan_expires_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "Never"}</span></div>
              {daysLeft != null && <div className="flex justify-between"><span className="text-slate-500">Days remaining</span><span className={cn("font-semibold", daysLeft <= 7 ? "text-amber-600" : "text-slate-800")} data-testid="sub-days-left">{daysLeft}</span></div>}
              <div className="flex justify-between"><span className="text-slate-500">Seat limit</span><span className="font-medium text-slate-800">{sub.seat_cap ?? "Unlimited"}</span></div>
              {sub.expired && <p className="text-xs text-rose-600 bg-rose-50 border border-rose-100 rounded-md px-3 py-2">Your access has expired. Contact the administrator to renew.</p>}
              {!sub.expired && daysLeft != null && daysLeft <= 7 && <p className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-md px-3 py-2">Your plan expires soon. Renew to avoid interruption.</p>}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
